/**
 * **The sitting's own log** (B0.7, docs/14-roadmap.md).
 *
 * D0's lesson, and the one M0 learned first: the answer to "was that hour any
 * good" should be a **log rather than a memory**. A player who has just spent
 * fifty minutes moving machines around cannot reliably say how many bottlenecks
 * they fixed, when the fab stopped improving, or whether they were still
 * enjoying it at minute forty — and a prototype's kill/continue decision should
 * not rest on any of that being remembered accurately.
 *
 * So the build writes the sitting down. The division of labour is the one the
 * determinism law forces (docs/13-architecture.md — no wall-clock in sim state
 * paths):
 *
 * - **The sim counts what only it can know**: layout edits, levers pulled, the
 *   throughput progression, bottlenecks identified-then-fixed, and every move's
 *   before/after delta. All of that arrives in the per-tick UI store, already
 *   computed, and this file only copies it.
 * - **The shell counts the clock**: when the sitting started, how long it has
 *   run, and how much of that the tab was actually in front of somebody. Real
 *   time is a measurement here, which is the one place it belongs.
 *
 * The file lands three ways, in descending order of convenience and ascending
 * order of reliability: posted to `/playtest` (the dev server writes it under
 * `web/playtest/`), parked on `window.yieldPlaytest`, and downloadable from the
 * scoreboard's own button. A sitting on a phone with no collector behind it is
 * still a sitting whose log can be got out.
 */

import type { CampaignUi, FloorUi, MoveUi, ScoreUi, SessionUi } from '../bridge';
import { snapshot } from './store';

/** How often a progression sample is taken, in real milliseconds. */
const SAMPLE_MS = 15_000;
/** How often the log is posted while the sitting runs. */
const POST_MS = 60_000;

/** One reading of the fab, stamped with both clocks. */
export interface ProgressionSample {
  /** Seconds of *real* time since the sitting began. */
  atRealSeconds: number;
  /** Theater seconds on the site's own clock. */
  theaterSeconds: number;
  wafersPerWeek: number;
  cycleHours: number;
  costPerWaferDollars: number;
  /** False while the floor is still rearranging after a change. */
  settled: boolean;
  /** The machine that was the constraint at the time. */
  bottleneck: string;
  /** Lot releases a day the fab was being run at. */
  startsPerDay: number;
  /** Agents on shift. */
  agents: number;
}

export interface PlaytestLog {
  /** Wall-clock, ISO. A measurement, never sim state. */
  startedAt: string;
  writtenAt: string;
  /** Real seconds since the sitting began, and how many of those the tab was
   *  visible — "time on site" and "time in the chair" are different numbers and
   *  a session log that conflated them would flatter itself. */
  realSeconds: number;
  visibleSeconds: number;
  userAgent: string;
  transport: string | null;
  isolated: boolean;
  /** What the site was asked for, and where it ended up. */
  objective: ScoreUi['objective'];
  score: ScoreUi | null;
  session: SessionUi | null;
  /** Every lever pulled, newest first, with its before/after delta. */
  moves: MoveUi[];
  progression: ProgressionSample[];
  /**
   * The campaign's own account of the sitting (M1.1): where the calendar got
   * to, how the weeks were carried, and what was saved. Grows package by
   * package rather than arriving at the verdict (docs/14-roadmap.md).
   */
  campaign: CampaignSitting | null;
}

export interface CampaignSitting {
  world: string;
  week: number;
  date: string;
  run: string;
  cashDollars: number;
  debtDollars: number;
  watchedWeeks: number;
  forwardedWeeks: number;
  /** The glued hash at the time of writing. */
  hash: string;
  /** Saves written and loads taken this sitting — shell counts, not sim state. */
  saves: number;
  loads: number;
  /** Weeks the calendar advanced during this sitting. */
  weeksThisSitting: number;
  /** M1.2: the second-hand market as last seen — fabs' worth on it and the
   * price — beside what the player took off it, net. Decisions that crossed
   * the seam, counted. */
  usedSupplyFabs: number;
  usedPriceMille: number;
  usedTakenFabs: number;
  /** M1.3: the quality spine as last seen — products designed, products on
   * the measured line, and its first assignment's parametric quality. */
  products: number;
  siteProducts: number;
  siteQualityMille: number;
  /** M1.5: the research pipeline as lived — projects completed over the
   * campaign, what is on the two queues right now, the library's size, and
   * the engineer pools. Decisions that crossed the research seam, counted. */
  projectsCompleted: number;
  processQueue: number;
  designQueue: number;
  libraryEntries: number;
  engineers: number;
}

declare global {
  interface Window {
    /** The sitting so far, for a device with no collector behind it. */
    yieldPlaytest?: () => PlaytestLog;
  }
}

let startedAt = 0;
let startedIso = '';
let visibleMs = 0;
let visibleSince = 0;
let lastSampleAt = 0;
let lastPostAt = 0;
let latest: FloorUi | null = null;
let latestCampaign: CampaignUi | null = null;
let firstWeek: number | null = null;
let saves = 0;
let loads = 0;
const progression: ProgressionSample[] = [];

/** The campaign's store, folded in beside the floor's (M1.1). */
export function recordCampaign(campaign: CampaignUi): void {
  latestCampaign = campaign;
  if (firstWeek === null) firstWeek = campaign.week;
}

/** A save was written, or a load taken; shell counts for the log. */
export function noteSave(): void {
  saves += 1;
}
export function noteLoad(): void {
  loads += 1;
  // A load moves the calendar under the sitting; the count restarts there.
  firstWeek = latestCampaign?.week ?? null;
}

/**
 * Begin the sitting. Called once at boot, before the first tick — everything
 * after it is timestamped against this.
 */
export function startPlaytest(): void {
  startedAt = performance.now();
  startedIso = new Date().toISOString();
  visibleSince = document.visibilityState === 'visible' ? startedAt : 0;
  lastSampleAt = startedAt;
  lastPostAt = startedAt;

  document.addEventListener('visibilitychange', () => {
    const now = performance.now();
    if (document.visibilityState === 'visible') {
      visibleSince = now;
    } else if (visibleSince > 0) {
      visibleMs += now - visibleSince;
      visibleSince = 0;
      // Leaving the tab is the most likely last thing that happens to a
      // sitting, so it is the moment worth writing on.
      void post(playtestLog());
    }
  });
  // The last word, on the way out. `sendBeacon` because a fetch started in
  // `pagehide` is not guaranteed to survive the page.
  window.addEventListener('pagehide', () => {
    const log = playtestLog();
    try {
      navigator.sendBeacon?.('/playtest', new Blob([JSON.stringify(log)], {
        type: 'application/json',
      }));
    } catch {
      /* nothing more to be done from a page that is going away */
    }
  });

  window.yieldPlaytest = playtestLog;
}

/**
 * Fold one tick of the UI store in. Called from the worker's tick callback, so
 * it runs at tick rate and never per frame — and it does almost nothing:
 * everything interesting was computed in the sim.
 */
export function recordTick(ui: FloorUi): void {
  latest = ui;
  const now = performance.now();
  if (now - lastSampleAt >= SAMPLE_MS) {
    lastSampleAt = now;
    progression.push(sample(ui, now));
  }
  if (now - lastPostAt >= POST_MS) {
    lastPostAt = now;
    void post(playtestLog());
  }
}

function sample(ui: FloorUi, now: number): ProgressionSample {
  return {
    atRealSeconds: Math.round((now - startedAt) / 1000),
    theaterSeconds: ui.theaterSeconds,
    wafersPerWeek: ui.score.wafersPerWeek,
    cycleHours: Number(ui.score.cycleHours.toFixed(2)),
    costPerWaferDollars: Number(ui.score.costPerWaferDollars.toFixed(2)),
    settled: ui.score.settled,
    bottleneck: ui.score.bottleneck,
    startsPerDay: ui.flow.startsPerDay,
    agents: ui.agents,
  };
}

/** The sitting as it stands. */
export function playtestLog(): PlaytestLog {
  const now = performance.now();
  const hud = snapshot();
  const visible = visibleMs + (visibleSince > 0 ? now - visibleSince : 0);
  return {
    startedAt: startedIso,
    writtenAt: new Date().toISOString(),
    realSeconds: Math.round((now - startedAt) / 1000),
    visibleSeconds: Math.round(visible / 1000),
    userAgent: navigator.userAgent,
    transport: hud.transport,
    isolated: hud.isolated,
    objective: latest?.score.objective ?? null,
    score: latest?.score ?? null,
    session: latest?.score.session ?? null,
    moves: latest?.score.moves ?? [],
    // A final sample, so the log's last row is where the sitting actually
    // ended rather than up to fifteen seconds before it.
    progression: latest ? [...progression, sample(latest, now)] : [...progression],
    campaign: latestCampaign
      ? {
          world: latestCampaign.world,
          week: latestCampaign.week,
          date: latestCampaign.date,
          run: latestCampaign.run,
          cashDollars: Math.round(latestCampaign.firm.cash),
          debtDollars: Math.round(latestCampaign.firm.debt),
          watchedWeeks: latestCampaign.watchedWeeks,
          forwardedWeeks: latestCampaign.forwardedWeeks,
          hash: latestCampaign.hash,
          saves,
          loads,
          weeksThisSitting: latestCampaign.week - (firstWeek ?? latestCampaign.week),
          usedSupplyFabs: latestCampaign.usedMarket.supplyFabs,
          usedPriceMille: latestCampaign.usedMarket.priceMille,
          usedTakenFabs: latestCampaign.usedMarket.takenFabs,
          products: latestCampaign.firm.products.length,
          siteProducts:
            latestCampaign.siteLine != null
              ? (latestCampaign.firm.lines.find((l) => l.index === latestCampaign!.siteLine)
                  ?.assignments.length ?? 0)
              : 0,
          siteQualityMille:
            latestCampaign.siteLine != null
              ? (latestCampaign.firm.lines.find((l) => l.index === latestCampaign!.siteLine)
                  ?.assignments[0]?.qualityMille ?? 0)
              : 0,
          projectsCompleted: latestCampaign.tech.completedProjects,
          processQueue: latestCampaign.tech.process.queue.length,
          designQueue: latestCampaign.tech.design.queue.length,
          libraryEntries: latestCampaign.tech.library.length,
          engineers:
            (latestCampaign.tech.process.engineers ?? 0) + (latestCampaign.tech.design.engineers ?? 0),
        }
      : null,
  };
}

/** Hand the browser the file. The button the player presses when they are done. */
export function downloadPlaytestLog(): void {
  const log = playtestLog();
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(log, null, 2)], { type: 'application/json' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = `yield-playtest-${log.startedAt.replace(/[:.]/g, '-')}.json`;
  link.click();
  URL.revokeObjectURL(url);
  void post(log);
}

/**
 * Post to whatever served the page. A failure is expected and ignored — a
 * sitting run from a file, a phone or a webview with no collector behind it
 * still has `window.yieldPlaytest()` and the download button.
 */
async function post(log: PlaytestLog): Promise<void> {
  try {
    await fetch('/playtest', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(log),
    });
  } catch {
    /* no collector listening */
  }
}
